import React, {useCallback, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {getAccountById, getAssertionsRelatedToAccount} from '../../../../selectors/Ares/selectors';
import {updateAccount} from '../../../../actions/Account/accountactions';
import {isNullOrUndefined, labels} from '../../../../util/utils';
import Tooltip from '../../BaseComponentWrappers/MotifTooltip';
import ToggleSwitch from '../../BaseComponentWrappers/MotifToggleSwitch';

const HasEstimate = ({className, accountId, hideToggle}) => {
	const {
		accountName,
		accountTypeId,
		engagementId,
		hasEstimate,
		statementTypeId,
		pspIndexes,
		priorHasEstimate,
		assertions: assertionIds
	} = useSelector((state) => getAccountById(state, accountId), shallowEqual) || {};
	const assertions = useSelector((state) => getAssertionsRelatedToAccount(state, assertionIds));

	const dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState(false);

	// Handle event when user click on estimate toggle
	const handleHasEstimate = useCallback(
		(event) => {
			setIsLoading(true);
			dispatch(
				updateAccount(engagementId, accountId, {
					accountName: accountName,
					accountType: accountTypeId || 0,
					statementType: statementTypeId,
					hasEstimate: event.target.checked,
					PSPIndexIds: pspIndexes.map((item) => item.knowledgeContentID),
					assertionTypes: assertions?.map((assertion) => assertion.knowledgeAssertionId),
					skipValidations: true // this is for time being until we finalize validation
				})
			).finally(() => {
				setIsLoading(false);
			});
		},
		[dispatch, accountName, accountTypeId, statementTypeId, engagementId]
	);

	if (hideToggle) {
		return (
			<StyledSection className={className}>
				{hasEstimate ? (
					<span className="hasestimates-label" title={labels.yes}>
						{labels.yes}
					</span>
				) : (
					<span className="hasestimates-label" title={labels.no}>
						{labels.no}
					</span>
				)}
			</StyledSection>
		);
	}
	return (
		<StyledSection className={className}>
			{!isNullOrUndefined(priorHasEstimate) ? (
				<Tooltip
					className={'hasestimate-tooltip'}
					variant="alt"
					closeButtonProps={{
						className: 'hide'
					}}
					allowClick={false}
					placement="top"
					trigger={
						<ToggleSwitch
							checked={hasEstimate}
							hideLabels
							onChange={handleHasEstimate}
							id={'toggleSwitchId' + accountId}
							disabled={isLoading}
						/>
					}
					children={
						<section className="tooltip-label">
							<span>{labels.priorYearEstimation}</span>
							<span>{priorHasEstimate ? ': ' + labels.yes : ': ' + labels.no}</span>
						</section>
					}
				/>
			) : (
				<ToggleSwitch
					checked={hasEstimate}
					hideLabels
					onChange={handleHasEstimate}
					id={'toggleSwitchId' + accountId}
					disabled={isLoading}
				/>
			)}
			{hasEstimate ? (
				<span className="hasestimates-label" title={labels.yes}>
					{labels.yes}
				</span>
			) : (
				<span className="hasestimates-label" title={labels.no}>
					{labels.no}
				</span>
			)}
		</StyledSection>
	);
};

HasEstimate.propTypes = {
	className: PropTypes.string,
	accountId: PropTypes.number.isRequired
};

HasEstimate.defaultProps = {
	className: 'hasestimates-cellrenderer'
};

export default HasEstimate;

const StyledSection = styled.section`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	.motif-toggle-switch {
		width: var(--px-41);
		height: var(--px-22);
		.motif-toggle-switch-slider {
			&::before {
				margin-left: var(--px-1);
				height: var(--px-18);
				width: var(--px-18);
			}
		}
		input:checked + .motif-toggle-switch-slider:before {
			transform: translateX(var(--px-29)) translateY(-50%);
			height: var(--px-18);
			width: var(--px-18);
		}
	}
	.hasestimates-label {
		font-weight: 600;
		font-size: var(--px-12);
		line-height: var(--px-16);
		display: inline-block;
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
		margin-left: var(--px-5);
		min-width: var(--px-30);
		width: auto;
		max-width: var(--px-80);
	}
	.tooltip-label {
		font-size: var(--px-12);
	}
`;
